import { defaultTo } from "lodash";
import { DateTime } from "luxon";
import { HospitalPrescriptionStatus } from "@common/models/Enums";
import { HospitalPrescriptionRequestListingModel } from "@common/models/HospitalPrescriptionRequestListingModel";
import { SalesItemTraitModel } from "@common/models/SalesItemTraitModel";
import { HospitalPrescriptionEventModel, IHospitalPrescriptionEventModel } from "./HospitalPrescriptionEventModel";
import { HospitalPrescriptionRefillModel, IHospitalPrescriptionRefillModel } from "./HospitalPrescriptionRefillModel";
import { HospitalPrescriptionStatusModel, IHospitalPrescriptionStatusModel } from "./HospitalPrescriptionStatusModel";
import { IModel, Model, ModelRecord } from "./Model";
import { IPhotoModel, PhotoModel } from "./PhotoModel";
import { ISalesItemOptionModel, SalesItemOptionModel } from "./SalesItemOptionModel";
import { IVisitSalesItemModel, VisitSalesItemModel } from "./VisitSalesItemModel";

export interface IHospitalPrescriptionModel extends IModel {
    hospitalPrescriptionId: number;
    prescribedProduct: string;
    remainingRefills: number;
    quantity: string | undefined;
    unitOfMeasure: string | undefined;
    dose: string | undefined;
    frequency: string;
    instructions: string;
    expirationDate: string;
    notes: string | undefined;
    lastFillDate: string | undefined
    salesDate: string;
    veterinarianName: string | undefined;
    fullName: string | undefined;
    optionDisplayName: string | undefined;
    fillByDate: string;
    completedDate: string;
    petId: number;
    visitSalesItemId: number | undefined;
    visitSalesItem: IVisitSalesItemModel | undefined;
    hospitalPrescriptionRefills: IHospitalPrescriptionRefillModel[];
    veterinarianSignaturePhotoId: number | undefined;
    veterinarianSignaturePhoto: IPhotoModel | undefined;
    veterinarianId: number | undefined;
    veterinarianFirstName: string | undefined;
    veterinarianLastName: string | undefined;
    veterinarianLicenseNumber: string | undefined;
    veterinarianLicenseStateId: number | undefined;
    veterinarianLicenseStateAbbreviation: string | undefined;
    veterinarianLicenseExpirationDate: string | undefined;
    veterinarianEmail: string | undefined;
    dispensingAuthUserId: number | undefined;
    dispensingUserFirstName: string | undefined;
    dispensingUserLastName: string | undefined;
    visitId: number | undefined;
    salesItemOptionId: number | undefined;
    salesItemOption: ISalesItemOptionModel | undefined;
    salesItemTraits: SalesItemTraitModel[] | undefined;
    hospitalPrescriptionStatusId: number | undefined;
    hospitalPrescriptionStatus: IHospitalPrescriptionStatusModel;
    orderNotes: string | undefined;
    dateCreated: string;
    dateUpdated: string;
    isHidden: boolean;

    hospitalPrescriptionPublicId: string | undefined;
    petcoSKU: string | undefined;
    petcoPrescriptionTypeName: string | undefined;
    vetLicenseLicenseTypeName: string | undefined;

    clientId: number | undefined;
    clientFirstName: string | undefined;
    clientLastName: string | undefined;
    clientEmailAddress: string | undefined;
    clientPhone: string | undefined;
    clientAddress: string | undefined;
    clientAddress2: string | undefined;
    clientCity: string | undefined;
    clientStateAbbreviation: string | undefined;
    clientZipCode: string | undefined;
    clientCounty: string | undefined;

    petName: string | undefined;
    petSpeciesName: string | undefined;
    petGenderName: string | undefined;
    petBreedName: string | undefined;
    petBirthDate: string | undefined;
    petWeight: number | undefined;
    petMedications: string | undefined;

    hospitalId: number | undefined;
    hospitalPublicId: string | undefined;
    hospitalCode: string | undefined;
    hospitalName: string | undefined;
    hospitalAddress: string | undefined;
    hospitalAddress2: string | undefined;
    hospitalCity: string | undefined;
    hospitalStateAbbreviation: string | undefined;
    hospitalZipCode: string | undefined;
    hospitalPhone: string | undefined;
    hospitalFax: string | undefined;
    storeName: string | undefined;
    storeBookingName: string | undefined;
    hospitalTimezoneName: string | undefined;
    hospitalLatitude: number | undefined;
    hospitalLongitude: number | undefined;

    hospitalPrescriptionEvents: IHospitalPrescriptionEventModel[];
}

export class HospitalPrescriptionModel extends Model implements ModelRecord<IHospitalPrescriptionModel> {
    hospitalPrescriptionId: number;
    prescribedProduct: string;
    remainingRefills: number;
    quantity: string | undefined;
    unitOfMeasure: string | undefined;
    dose: string | undefined;
    frequency: string | undefined;
    instructions: string | undefined;
    expirationDate: DateTime;
    notes: string | undefined;
    lastFillDate: DateTime | undefined;
    salesDate: DateTime;
    veterinarianName: string | undefined;
    fullName: string | undefined;
    optionDisplayName: string | undefined;
    fillByDate: DateTime | undefined;
    completedDate: DateTime | undefined;
    petId: number;
    visitSalesItemId: number | undefined;
    visitSalesItem: VisitSalesItemModel | undefined
    hospitalPrescriptionRefills: HospitalPrescriptionRefillModel[];
    veterinarianSignaturePhotoId: number | undefined;
    veterinarianSignaturePhoto: PhotoModel | undefined;
    veterinarianId: number | undefined;
    veterinarianFirstName: string | undefined;
    veterinarianLastName: string | undefined;
    veterinarianLicenseNumber: string | undefined;
    veterinarianLicenseStateId: number | undefined;
    veterinarianLicenseStateAbbreviation: string | undefined;
    veterinarianLicenseExpirationDate: DateTime | undefined;
    veterinarianEmail: string | undefined;
    dispensingAuthUserId: number | undefined;
    dispensingUserFirstName: string | undefined;
    dispensingUserLastName: string | undefined;
    visitId: number | undefined;
    salesItemOptionId: number | undefined;
    salesItemOption: SalesItemOptionModel | undefined;
    salesItemTraits: SalesItemTraitModel[] | undefined;
    hospitalPrescriptionStatusId: number | undefined;
    hospitalPrescriptionStatus: HospitalPrescriptionStatusModel;
    orderNotes: string | undefined;
    dateCreated: DateTime | undefined;
    dateUpdated: DateTime | undefined;
    isHidden: boolean;

    hospitalPrescriptionPublicId: string | undefined;
    petcoSKU: string | undefined;
    petcoPrescriptionTypeName: string | undefined;
    vetLicenseLicenseTypeName: string | undefined;

    clientId: number | undefined;
    clientFirstName: string | undefined;
    clientLastName: string | undefined;
    clientEmailAddress: string | undefined;
    clientPhone: string | undefined;
    clientAddress: string | undefined;
    clientAddress2: string | undefined;
    clientCity: string | undefined;
    clientStateAbbreviation: string | undefined;
    clientZipCode: string | undefined;
    clientCounty: string | undefined;

    petName: string | undefined;
    petSpeciesName: string | undefined;
    petGenderName: string | undefined;
    petBreedName: string | undefined;
    petBirthDate: DateTime | undefined;
    petWeight: number | undefined;
    petMedications: string | undefined;

    hospitalId: number | undefined;
    hospitalPublicId: string | undefined;
    hospitalCode: string | undefined;
    hospitalName: string | undefined;
    hospitalAddress: string | undefined;
    hospitalAddress2: string | undefined;
    hospitalCity: string | undefined;
    hospitalStateAbbreviation: string | undefined;
    hospitalZipCode: string | undefined;
    hospitalPhone: string | undefined;
    hospitalFax: string | undefined;
    storeName: string | undefined;
    storeBookingName: string | undefined;
    hospitalTimezoneName: string | undefined;
    hospitalLatitude: number | undefined;
    hospitalLongitude: number | undefined;

    hospitalPrescriptionEvents: HospitalPrescriptionEventModel[];

    constructor(params?: Partial<IHospitalPrescriptionModel>) {
        super(params);
        this.hospitalPrescriptionId = defaultTo(params?.hospitalPrescriptionId, 0);
        this.prescribedProduct = defaultTo(params?.prescribedProduct, "");
        this.remainingRefills = defaultTo(params?.remainingRefills, 0);
        this.quantity = params?.quantity;
        this.unitOfMeasure = params?.unitOfMeasure;
        this.dose = params?.dose;
        this.frequency = params?.frequency;
        this.instructions = params?.instructions;
        this.expirationDate = params?.expirationDate ? DateTime.fromISO(params?.expirationDate, { zone: "utc" }) : DateTime.utc().plus({ year: 1 });
        this.notes = params?.notes;
        this.lastFillDate = params?.lastFillDate ? DateTime.fromISO(params?.lastFillDate) : undefined;
        this.salesDate = params?.salesDate ? DateTime.fromISO(params?.salesDate, { zone: "utc" }) : DateTime.utc();
        this.veterinarianName = params?.veterinarianName;
        this.fullName = params?.fullName;
        this.optionDisplayName = params?.optionDisplayName;
        this.fillByDate = params?.fillByDate ? DateTime.fromISO(params?.fillByDate) : undefined;
        this.completedDate = params?.completedDate ? DateTime.fromISO(params?.completedDate) : undefined;
        this.petId = defaultTo(params?.petId, 0);
        this.visitSalesItemId = params?.visitSalesItemId;
        this.visitSalesItem = params?.visitSalesItem ? new VisitSalesItemModel(params.visitSalesItem) : undefined
        this.hospitalPrescriptionRefills = defaultTo(params?.hospitalPrescriptionRefills, []).map(a => new HospitalPrescriptionRefillModel(a));
        this.veterinarianSignaturePhotoId = params?.veterinarianSignaturePhotoId;
        this.veterinarianSignaturePhoto = params?.veterinarianSignaturePhoto ? new PhotoModel(params.veterinarianSignaturePhoto) : undefined
        this.veterinarianId = params?.veterinarianId;
        this.veterinarianFirstName = params?.veterinarianFirstName;
        this.veterinarianLastName = params?.veterinarianLastName;
        this.veterinarianLicenseNumber = params?.veterinarianLicenseNumber;
        this.veterinarianLicenseStateId = params?.veterinarianLicenseStateId;
        this.veterinarianLicenseStateAbbreviation = params?.veterinarianLicenseStateAbbreviation;
        this.veterinarianLicenseExpirationDate = params?.veterinarianLicenseExpirationDate ? DateTime.fromISO(params?.veterinarianLicenseExpirationDate) : undefined;
        this.veterinarianEmail = params?.veterinarianEmail;
        this.dispensingAuthUserId = params?.dispensingAuthUserId;
        this.dispensingUserFirstName = params?.dispensingUserFirstName;
        this.dispensingUserLastName = params?.dispensingUserLastName;
        this.visitId = params?.visitId;
        this.salesItemOptionId = params?.salesItemOptionId;
        this.salesItemOption = params?.salesItemOption ? new SalesItemOptionModel(params.salesItemOption) : undefined
        this.salesItemTraits = params?.salesItemTraits ? params?.salesItemTraits.map(t => new SalesItemTraitModel(t)) : undefined
        this.hospitalPrescriptionStatusId = params?.hospitalPrescriptionStatusId;
        this.hospitalPrescriptionStatus = new HospitalPrescriptionStatusModel(params?.hospitalPrescriptionStatus);
        this.orderNotes = params?.orderNotes;
        this.dateCreated = params?.dateCreated ? DateTime.fromISO(params?.dateCreated, { zone: "utc" }) : undefined;
        this.dateUpdated = params?.dateUpdated ? DateTime.fromISO(params?.dateUpdated, { zone: "utc" }) : undefined;
        this.isHidden = defaultTo(params?.isHidden, false);

        this.hospitalPrescriptionPublicId = params?.hospitalPrescriptionPublicId;
        this.petcoSKU = params?.petcoSKU;
        this.petcoPrescriptionTypeName = params?.petcoPrescriptionTypeName;
        this.vetLicenseLicenseTypeName = params?.vetLicenseLicenseTypeName;

        this.clientId = params?.clientId;
        this.clientFirstName = params?.clientFirstName;
        this.clientLastName = params?.clientLastName;
        this.clientEmailAddress = params?.clientEmailAddress;
        this.clientPhone = params?.clientPhone;
        this.clientAddress = params?.clientAddress;
        this.clientAddress2 = params?.clientAddress2;
        this.clientCity = params?.clientCity;
        this.clientStateAbbreviation = params?.clientStateAbbreviation;
        this.clientZipCode = params?.clientZipCode;
        this.clientCounty = params?.clientCounty;

        this.petName = params?.petName;
        this.petSpeciesName = params?.petSpeciesName;
        this.petGenderName = params?.petGenderName;
        this.petBreedName = params?.petBreedName;
        this.petBirthDate = params?.petBirthDate ? DateTime.fromISO(params?.petBirthDate, { zone: "utc" }) : undefined;
        this.petWeight = defaultTo(params?.petWeight, 0);
        this.petMedications = params?.petMedications;

        this.hospitalId = params?.hospitalId;
        this.hospitalPublicId = params?.hospitalPublicId;
        this.hospitalCode = params?.hospitalCode;
        this.hospitalName = params?.hospitalName;
        this.hospitalAddress = params?.hospitalAddress;
        this.hospitalAddress2 = params?.hospitalAddress2;
        this.hospitalCity = params?.hospitalCity;
        this.hospitalStateAbbreviation = params?.hospitalStateAbbreviation;
        this.hospitalZipCode = params?.hospitalZipCode;
        this.hospitalPhone = params?.hospitalPhone;
        this.hospitalFax = params?.hospitalFax;
        this.storeName = params?.storeName;
        this.storeBookingName = params?.storeBookingName;
        this.hospitalTimezoneName = params?.hospitalTimezoneName;
        this.hospitalLatitude = params?.hospitalLatitude;
        this.hospitalLongitude = params?.hospitalLongitude;

        this.hospitalPrescriptionEvents = defaultTo(params?.hospitalPrescriptionEvents, []).map(e => new HospitalPrescriptionEventModel(e));
    }

    get isActive(): boolean {
        return this.hospitalPrescriptionId !== undefined && this.isExpired == false && Number(this.remainingRefills) > 0
            && this.hospitalPrescriptionStatusId !== HospitalPrescriptionStatus.Cancelled
            && (this.hospitalPrescriptionStatusId === undefined
                || [HospitalPrescriptionStatus.Approved,
                HospitalPrescriptionStatus.ApprovedPendingPickup,
                HospitalPrescriptionStatus.Recalled,
                HospitalPrescriptionStatus.Transferred,
                HospitalPrescriptionStatus.TransferredPermanent].some(s => s === this.hospitalPrescriptionStatusId))
    }

    get isExpired(): boolean {
        return this.expirationDate < DateTime.utc().startOf('day');
    }

    toRefillRequestModel(): HospitalPrescriptionRequestListingModel {
        return new HospitalPrescriptionRequestListingModel({
            hospitalPrescriptionId: this.hospitalPrescriptionId,
            hospitalPrescriptionStatusId: this.hospitalPrescriptionStatusId,
            hospitalPrescriptionRefillId: undefined,
            hospitalPrescriptionRefillStatusId: undefined,
            prescriptionName: "",
            expiresDate: this.expirationDate.toISO(),
            refills: defaultTo(this.remainingRefills, 0).toString(),
            notes: defaultTo(this.notes, ""),
            instructions: defaultTo(this.instructions, ""),
            salesDate: this.salesDate.toLocaleString(DateTime.DATE_SHORT),
            veterinarianId: defaultTo(this.veterinarianId, 0),
            veterinarianName: defaultTo(this.veterinarianName, ""),
            veterinarianFirstName: defaultTo(this.veterinarianFirstName, ""),
            veterinarianLastName: defaultTo(this.veterinarianLastName, ""),
            veterinarianLicenseNumber: defaultTo(this.veterinarianLicenseNumber, ""),
            veterinarianLicenseStateId: defaultTo(this.veterinarianLicenseStateId, 0),
            veterinarianLicenseStateAbbreviation: defaultTo(this.veterinarianLicenseStateAbbreviation, ""),
            veterinarianLicenseExpirationDate: undefined,
            veterinarianEmail: defaultTo(this.veterinarianEmail, ""),
            veterinarianSignaturePhotoId: defaultTo(this.veterinarianSignaturePhotoId, 0),
            veterinarianSignaturePhotoURL: defaultTo(this.veterinarianSignaturePhoto?.photoUrl, ""),
            fullName: defaultTo(this.fullName, ""),
            unitOfMeasure: defaultTo(this.unitOfMeasure, ""),
            dose: defaultTo(this.dose, ""),
            frequency: defaultTo(this.frequency, ""),
            optionDisplayName: this.optionDisplayName,
            clientId: defaultTo(this.clientId, 0),
            clientFirstName: defaultTo(this.clientFirstName, ""),
            clientLastName: defaultTo(this.clientLastName, ""),
            clientAddress: defaultTo(this.clientAddress, ""),
            clientCity: defaultTo(this.clientCity, ""),
            clientState: defaultTo(this.clientStateAbbreviation, ""),
            clientZipcode: defaultTo(this.clientZipCode, ""),
            petId: defaultTo(this.petId, 0),
            petName: defaultTo(this.petName, ""),
            petSpecies: defaultTo(this.petSpeciesName, ""),
            petBreed: defaultTo(this.petBreedName, ""),
            petBirthDate: this.petBirthDate ? this.petBirthDate.toISO() : "",
            petWeight: defaultTo(this.petWeight, 0),
            quantity: defaultTo(this.quantity, ""),
            dateRequested: '',
            rowVersion: '',
            dateUpdated: "",
            dateCreated: "",
            fillByDate: this.fillByDate ? this.fillByDate.toLocaleString(DateTime.DATE_SHORT) : "",
            completedDate: "",
            orderNotes: this.orderNotes,
            creatorFirstName: undefined,
            creatorLastName: undefined,
            creatorTitle: undefined,
            hospitalPrescriptionRowVersion: defaultTo(this.rowVersion, ""),
            salesItemOptionId: defaultTo(this.salesItemOptionId, 0)
        })
    }
}
